<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useRequest } from '@/composables/useRequest';
import PromoCard from '@/components/cards/PromoCard.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
defineProps({
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const tabs = [
  { id: 'current', title: t('promotion.current') },
  { id: 'archive', title: t('promotion.archive') },
];
const route = useRoute();
const router = useRouter();

const activeTab = ref(route.query.type === 'archive' ? 'archive' : 'current');

const selectedYear = ref(Number(route.query.year) || 'all');
const perPage = ref(5);

const getInitialFirst = () => {
  const pageQuery = parseInt(route.query.page);
  return pageQuery > 1 ? (pageQuery - 1) * perPage.value : 0;
};

const first = ref(getInitialFirst());
const currentPage = computed(() => Math.floor(first.value / perPage.value) + 1);

const buildRequestParams = () => {
  const params = {};

  if (currentPage.value > 1) params.page = currentPage.value;
  if (selectedYear.value !== 'all') params.year = selectedYear.value;
  if (activeTab.value === 'archive') params.type = 'archive';

  return params;
};

const { data: response, pending } = await useAsyncData(
  'promos-list',
  () =>
    useRequest('promos', {
      params: buildRequestParams(),
    }),
  {
    watch: [currentPage, selectedYear, activeTab],
  },
);

const results = computed(() => response.value?.items || []);
const totalRecords = computed(() => response.value?.count || 0);

const years = computed(() => {
  const apiYears = response.value?.years || [];
  if (!apiYears.length) return [];
  const sortedYears = [...apiYears].sort((a, b) => b - a);
  return [
    { label: t('promotion.all_years'), value: 'all' },
    ...sortedYears.map((y) => ({
      label: y.toString(),
      value: y,
    })),
  ];
});
watch(
  () => response.value?.pagination?.per_page,
  (newPerPage) => {
    if (newPerPage) {
      const current = currentPage.value;
      perPage.value = newPerPage;
      first.value = (current - 1) * newPerPage;
    }
  },
  { immediate: true },
);
watch([currentPage, activeTab, selectedYear], ([page, tab, year]) => {
  const query = { ...route.query };

  if (page > 1) query.page = page.toString();
  else delete query.page;

  if (tab === 'archive') query.type = 'archive';
  else delete query.type;

  if (year !== 'all') query.year = year;
  else delete query.year;

  router.push({ query });
});

const changeTab = (id) => {
  activeTab.value = id;
  first.value = 0;
  selectedYear.value = 'all';
};

const onYearChange = () => {
  first.value = 0;
};

const tabListElement = useTemplateRef('tabList');
const onTabListResize = () => tabListElement.value?.updateInkBar();

watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});

onMounted(() =>
  window.addEventListener('resize', onTabListResize, { passive: true }),
);
onBeforeUnmount(() => window.removeEventListener('resize', onTabListResize));

const lastPage = computed(() => Math.ceil(totalRecords.value / perPage.value));

const getPageQuery = (page) => {
  const query = { ...route.query };
  if (page > 1) query.page = page.toString();
  else delete query.page;
  return query;
};

const visiblePages = computed(() => {
  const current = currentPage.value;
  const last = lastPage.value;
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last; i++) {
    if (
      i === 1 ||
      i === last ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

watch(
  () => route.query.page,
  (newPage) => {
    const p = parseInt(newPage) || 1;
    first.value = (p - 1) * perPage.value;
  },
);
</script>

<template>
  <Tabs v-model:value="activeTab" class="promo__tabs" :scrollable="true">
    <TabList
      ref="tabList"
      :class="`promo__tabs-headers tabs-bg-${backgroundColor}`"
    >
      <Tab
        v-for="tab in tabs"
        :key="tab.id"
        :value="tab.id"
        class="promo__tabs-title"
        @click="changeTab(tab.id)"
      >
        {{ tab.title }}
      </Tab>
    </TabList>

    <TabPanels class="promo__tabs-content" :class="{ 'is-loading': pending }">
      <TabPanel v-for="tab in tabs" :key="tab.id" :value="tab.id">
        <div
          v-if="pending"
          class="promo__overlay"
          :class="`promo__overlay--${backgroundColor || 'white'}`"
        >
          <div class="promo__overlay-spinner"></div>
        </div>
        <FloatLabel
          v-if="years.length > 2"
          class="promo__years"
          :class="`floatlabel-on${backgroundColor || 'white'} promo__overlay--${backgroundColor || 'white'}`"
          variant="on"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <Select
            v-model="selectedYear"
            input-id="selectedYear"
            :options="years"
            option-label="label"
            option-value="value"
            variant="filled"
            :class="`select-on${backgroundColor || 'white'}`"
            class="promo__years-select"
            @change="onYearChange"
          >
            <template #dropdownicon>
              <CIcon name="direction-black" width="16" height="16" />
            </template>
          </Select>
          <label for="selectedYear" class="promo__years-label">
            {{ $t('promotion.choose_year') }}
          </label>
        </FloatLabel>

        <div class="promo__items">
          <template v-if="results.length > 0">
            <PromoCard
              v-for="(promo, index) in results"
              :key="index"
              :data="promo"
              :background-color="backgroundColor"
              data-aos="fade-up"
              :data-aos-delay="index >= 1 ? 100 + 100 * index : 100"
            />
          </template>
          <div
            v-else
            class="promo__items--empty"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          >
            {{ $t('promotion.empty') }}
          </div>
        </div>

        <nav
          v-if="lastPage > 1"
          class="promo__pagination"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <NuxtLink
            :to="{ query: getPageQuery(currentPage - 1) }"
            class="promo__pagination-btn promo__pagination-btn--prev"
            :class="{ 'promo__pagination-btn--disabled': currentPage === 1 }"
            rel="prev"
          >
            <CIcon
              class="promo__pagination-btn-icon"
              name="direction-black"
              width="16"
              height="16"
            />
          </NuxtLink>

          <div class="promo__pagination-items">
            <template v-for="(page, idx) in visiblePages" :key="idx">
              <span
                v-if="page === '...'"
                class="promo__pagination-item promo__pagination-item--dots"
              >
                ...
              </span>
              <NuxtLink
                v-else
                :to="{ query: getPageQuery(page) }"
                :class="[
                  'promo__pagination-item',
                  { 'promo__pagination-item--active': page === currentPage },
                ]"
              >
                {{ page }}
              </NuxtLink>
            </template>
          </div>

          <NuxtLink
            :to="{ query: getPageQuery(currentPage + 1) }"
            class="promo__pagination-btn promo__pagination-btn--next"
            :class="{
              'promo__pagination-btn--disabled': currentPage === lastPage,
            }"
            rel="next"
          >
            <CIcon
              class="promo__pagination-btn-icon"
              name="direction-black"
              width="16"
              height="16"
            />
          </NuxtLink>
        </nav>
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>

<style scoped lang="scss">
@keyframes loader-spin {
  to {
    transform: rotate(360deg);
  }
}
.promo {
  $parent: &;
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
    &-spinner {
      animation: loader-spin 0.8s linear infinite;
      border: 3px solid $divider;
      border-radius: 50%;
      border-top-color: $black;
      height: 40px;
      margin: 0 auto;
      position: sticky;
      top: calc(50vh - 20px);
      width: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 3);
        @include responsive-spacing(height, 40);
        @include responsive-spacing(width, 40);
        top: calc(50vh - px-to-vw(20));
      }
    }
  }

  &__container {
    display: flex;
    flex-direction: column;
    gap: 32px;
    position: relative;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 32);
    }
  }
  &__years {
    @include media-breakpoint-up(sm) {
      margin: 0 auto;
      width: 32.8%;
    }
  }
  &__items {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: relative;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
  &__tabs {
    flex: 1 1 auto;
    min-width: 0;
    @include media-breakpoint-up(sm) {
      display: flex;
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-content) {
        border-bottom: 1px solid $divider;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-bottom-width, 1);
        }
      }
      &:deep(.p-tablist-tab-list) {
        justify-content: center;
        gap: 8px;
        @include media-breakpoint-up(md) {
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }

      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      padding: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
      }
    }
    &-content {
      padding-top: 32px;
      position: relative;
      @include media-breakpoint-up(sm) {
        padding-top: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 48);
      }
      &:deep(.p-tabpanel) {
        display: flex;
        flex-direction: column;
        gap: 32px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 32);
        }
      }
    }
  }
  &__pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 25px 16px 24px;
    @include media-breakpoint-up(sm) {
      gap: 28px;
      padding: 41px 48px 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 28);
      @include responsive-spacing(padding-top, 41);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(padding-bottom, 40);
    }
    &-items {
      align-items: center;
      display: flex;
      gap: 4px;
      list-style: none;
      margin: 0;
      padding: 0;
      @include media-breakpoint-up(sm) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
    }
    &-btn {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 32px;
      width: 32px;
      @include media-breakpoint-up(sm) {
        border: 1px solid $outline !important;
        border-radius: 50%;
        height: 48px;
        width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(width, 48);
      }
      &--disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &--prev {
        #{$parent} {
          &__pagination-btn {
            &-icon {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    &-item {
      margin: 0;
      padding: 0;
      @include link-normal;
      align-items: center;
      border-radius: 50%;
      display: inline-flex;
      justify-content: center;
      height: 32px;
      min-width: 24px;
      text-decoration: none;
      @include media-breakpoint-up(sm) {
        border: 1px solid $divider;
        height: 48px;
        min-width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(min-width, 48);
      }
      &--active {
        background-color: $black;
        border-radius: 50%;
        color: $white;
        min-width: 32px;
        @include media-breakpoint-up(sm) {
          border: 1px solid $black;
          min-width: 48px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-width, 1);
          @include responsive-spacing(min-width, 48);
        }
      }
      &--dots {
        @include link-normal;
        display: inline-flex;
        min-width: 24px;
        justify-content: center;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(min-width, 24);
        }
      }
    }
  }
}
</style>
